// CLOSER/KEYSTONE Bookings console — sub-stage board + state pipeline + a
// detail drawer with P&L, legs, documents, payments (refund) and a Record
// Payment form (Virtual Terminal / wire) with idempotent-replay UX.
//
// OPS-ONLY surface: the board + drawer show margin/P&L (money roles, gated by
// the shared user context from AppShell AND server-side require_roles).
// Live polling via react-query (5-10s refetchInterval).
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, X, FileText, CreditCard, Undo2, AlertTriangle } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import StatusPill from "@/components/StatusPill";
import { useUser } from "@/components/AppShell";
import { admin, ops } from "@/lib/api";
import { fmtTime, fmtUSD, shortId } from "@/lib/format";

const STATES = ["hold", "confirmed", "in_fulfillment", "flown", "cancelled"];
const STATE_COLOR = {
  hold: "rgb(176,116,18)",
  confirmed: "rgb(32,138,78)",
  in_fulfillment: "rgb(36,104,184)",
  flown: "rgb(32,138,78)",
  cancelled: "rgb(196,52,44)",
};

const SUBSTAGES = [
  "awaiting_compliance",
  "awaiting_signature",
  "awaiting_payment",
  "payment_failed",
  "ready_for_dispatch",
];
const SUBSTAGE_COLOR = {
  awaiting_compliance: "rgb(196,52,44)",
  awaiting_signature: "rgb(176,116,18)",
  awaiting_payment: "rgb(36,104,184)",
  payment_failed: "rgb(196,52,44)",
  ready_for_dispatch: "rgb(32,138,78)",
};

const MONEY_ROLES = ["admin", "ops", "finance"];
const RECORD_ROLES = ["admin", "finance"]; // POST record-payment / refund roles

function fmtAge(min) {
  if (min == null) return "—";
  if (min < 60) return `${Math.round(min)}m`;
  if (min < 60 * 48) return `${(min / 60).toFixed(1)}h`;
  return `${(min / 1440).toFixed(1)}d`;
}

export default function Bookings() {
  const [view, setView] = useState("board"); // board (sub-stage) | pipeline (state)
  const [openId, setOpenId] = useState(null);

  const { data: rows = [] } = useQuery({
    queryKey: ["bookings"],
    queryFn: () => admin.bookings({ limit: 300 }),
    refetchInterval: 8000,
    retry: false,
  });
  const { data: board } = useQuery({
    queryKey: ["bookings-board"],
    queryFn: ops.bookingsBoard,
    refetchInterval: 8000,
    retry: false,
  });

  const byState = STATES.reduce((acc, s) => ({ ...acc, [s]: rows.filter((r) => r.state === s) }), {});
  const open = openId ? rows.find((r) => r.id === openId) || { id: openId } : null;

  return (
    <div data-testid="page-bookings" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· CLOSER · Pipeline</div>
          <h1 className="font-display text-[40px] leading-[1.02]">Bookings</h1>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex">
            {["board", "pipeline"].map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`view-${v}`}
                onClick={() => setView(v)}
                className="mono px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-[rgb(var(--border-strong))]"
                style={
                  view === v
                    ? { background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }
                    : { color: "rgb(var(--dim))" }
                }
              >
                {v === "board" ? "Sub-stage board" : "State pipeline"}
              </button>
            ))}
          </div>
          <MockBadge />
        </div>
      </div>

      {view === "board" && (
        <div className="overflow-x-auto pb-4" data-testid="substage-board">
          <div className="flex gap-4 min-w-max">
            {SUBSTAGES.map((s) => {
              const cards = board?.columns?.[s] || [];
              return (
                <div key={s} className="w-72 shrink-0" data-testid={`substage-${s}`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] flex items-center gap-2 min-w-0">
                      <span className="shrink-0" style={{ width: 8, height: 8, background: SUBSTAGE_COLOR[s], display: "inline-block" }} />
                      <span className="truncate">{s.replaceAll("_", " ")}</span>
                    </div>
                    <span className="mono text-[10px] text-[rgb(var(--dim))] shrink-0">{board?.counts?.[s] ?? 0}</span>
                  </div>
                  <div className="space-y-2">
                    {cards.length === 0 && (
                      <div className="pj-card p-3 mono text-[11px] text-[rgb(var(--muted-2))]">Empty.</div>
                    )}
                    {cards.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`board-booking-${b.id}`}
                        onClick={() => setOpenId(b.id)}
                        className="w-full text-left pj-card p-3 hover:border-[rgb(var(--border-strong))]"
                        style={{ borderLeft: `2px solid ${SUBSTAGE_COLOR[s]}` }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="mono text-[10px] text-[rgb(var(--dim))] truncate">{shortId(b.id)}</div>
                          {/* Sub-stage chips: age vs SLA, breach flag */}
                          <span
                            className="mono text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 shrink-0 inline-flex items-center gap-1"
                            style={{
                              background: b.breached ? "rgb(196,52,44)" : "rgb(236,229,216)",
                              color: b.breached ? "rgb(252,249,243)" : "rgb(var(--dim))",
                            }}
                          >
                            {b.breached && <AlertTriangle size={9} />}
                            {fmtAge(b.age_minutes)} / {fmtAge(b.sla_minutes)}
                          </span>
                        </div>
                        <div className="font-display text-[15px] mt-1 truncate">{fmtUSD(b.client_total_usd)}</div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <StatusPill state={b.state} />
                          <span className="mono text-[10px] text-[rgb(var(--dim))] shrink-0 whitespace-nowrap">
                            Margin {b.margin_usd != null ? fmtUSD(b.margin_usd) : "—"}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "pipeline" && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {STATES.map((s) => (
              <div key={s} className="w-72 shrink-0" data-testid={`column-${s}`}>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] flex items-center gap-2 min-w-0">
                    <span className="shrink-0" style={{ width: 8, height: 8, background: STATE_COLOR[s], display: "inline-block" }} />
                    <span className="truncate">{s.replace("_", " ")}</span>
                  </div>
                  <span className="mono text-[10px] text-[rgb(var(--dim))] shrink-0">{byState[s].length}</span>
                </div>
                <div className="space-y-2">
                  {byState[s].length === 0 && (
                    <div className="pj-card p-3 mono text-[11px] text-[rgb(var(--muted-2))]">No bookings.</div>
                  )}
                  {byState[s].map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`booking-${b.id}`}
                      onClick={() => setOpenId(b.id)}
                      className="w-full text-left pj-card p-3 hover:border-[rgb(var(--border-strong))]"
                      style={{ borderLeft: `2px solid ${STATE_COLOR[s]}` }}
                    >
                      <div className="mono text-[10px] text-[rgb(var(--dim))] truncate">{shortId(b.id)}</div>
                      <div className="font-display text-[15px] mt-1 truncate">{fmtUSD(b.client_total_usd)}</div>
                      <div className="mono text-[10px] text-[rgb(var(--dim))] mt-1 truncate">{fmtTime(b.created_at)}</div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <StatusPill state={b.state} />
                        <span className="mono text-[10px] text-[rgb(var(--dim))] shrink-0 whitespace-nowrap">
                          Margin {b.margin_usd ? fmtUSD(b.margin_usd) : "—"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length === 0 && (
        <div className="pj-card p-10 text-center mono text-[12px] text-[rgb(var(--dim))]">
          <Briefcase size={18} className="inline mb-2 block mx-auto text-[rgb(var(--muted-2))]" />
          No bookings yet. Open a Quote → Accept to create one.
        </div>
      )}

      {open && <BookingDrawer booking={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer — P&L / legs / documents / payments / record payment / refund
// ---------------------------------------------------------------------------
function BookingDrawer({ booking, onClose }) {
  const user = useUser();
  const qc = useQueryClient();
  const canSeeMoney = MONEY_ROLES.includes(user?.role);
  const canRecord = RECORD_ROLES.includes(user?.role);

  const { data: pnl } = useQuery({
    queryKey: ["booking-pnl", booking.id],
    queryFn: () => admin.bookingPnL(booking.id),
    enabled: canSeeMoney,
    refetchInterval: 10000,
    retry: false,
  });
  const { data: docs } = useQuery({
    queryKey: ["booking-docs", booking.id],
    queryFn: () => ops.bookingDocuments(booking.id),
    refetchInterval: 10000,
    retry: false,
  });
  const { data: paymentsData } = useQuery({
    queryKey: ["booking-payments", booking.id],
    queryFn: () => ops.bookingPayments(booking.id),
    refetchInterval: 8000,
    retry: false,
  });
  const { data: trip } = useQuery({
    queryKey: ["booking-trip", booking.trip_request_id],
    queryFn: () => admin.trip(booking.trip_request_id),
    enabled: Boolean(booking.trip_request_id),
    retry: false,
  });

  const payments = paymentsData?.payments || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["booking-payments", booking.id] });
    qc.invalidateQueries({ queryKey: ["booking-pnl", booking.id] });
    qc.invalidateQueries({ queryKey: ["bookings"] });
    qc.invalidateQueries({ queryKey: ["bookings-board"] });
  };

  const refund = async (paymentId) => {
    try {
      await ops.refundPayment(paymentId);
      toast.success("Refund issued — payout cancelled, booking cancelled");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid={`booking-drawer-${booking.id}`}>
      <div className="absolute inset-0" style={{ background: "rgba(20,17,13,0.45)" }} onClick={onClose} aria-hidden />
      <aside
        className="relative h-full w-full max-w-[480px] overflow-y-auto p-6"
        style={{ background: "rgb(var(--surface))", borderLeft: "1px solid rgb(var(--ln))" }}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="pj-label mb-1">Booking · {shortId(booking.id)}</div>
            <h2 className="font-display text-[24px]">{fmtUSD(booking.client_total_usd)}</h2>
            <div className="mt-1"><StatusPill state={booking.state} /></div>
          </div>
          <button type="button" onClick={onClose} data-testid="drawer-close" className="p-1.5 hover:bg-[rgb(236,229,216)]" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* P&L — money roles only (BACK-STAGE: never client-facing) */}
        {canSeeMoney && (
          <section className="pj-card p-4 mb-4" data-testid="drawer-pnl">
            <div className="pj-label mb-2">Trip P&L · back-stage</div>
            <div className="grid grid-cols-2 gap-2 mono text-[12px]">
              <div>Revenue<div className="text-[14px] mt-0.5">{fmtUSD(pnl?.revenue_usd)}</div></div>
              <div>Operator cost<div className="text-[14px] mt-0.5">{fmtUSD(pnl?.operator_cost_usd)}</div></div>
              <div>Gross margin<div className="text-[14px] mt-0.5">{fmtUSD(pnl?.gross_margin_usd)}</div></div>
              <div>Margin %<div className="text-[14px] mt-0.5">{pnl?.gross_margin_pct != null ? `${pnl.gross_margin_pct}%` : "—"}</div></div>
            </div>
          </section>
        )}

        {/* Legs (from the originating trip request) */}
        <section className="pj-card p-4 mb-4" data-testid="drawer-legs">
          <div className="pj-label mb-2">Legs</div>
          {(trip?.legs || []).length === 0 && (
            <div className="mono text-[11px] text-[rgb(var(--dim))]">No legs on file.</div>
          )}
          {(trip?.legs || []).map((l, i) => (
            <div key={`${l.from_airport}-${i}`} className="mono text-[12px] py-1 flex items-center justify-between gap-2">
              <span>{l.from_airport} → {l.to_airport}</span>
              <span className="text-[rgb(var(--dim))] text-[10px]">{fmtTime(l.departure_at)}</span>
            </div>
          ))}
        </section>

        {/* Documents */}
        <section className="pj-card p-4 mb-4" data-testid="drawer-documents">
          <div className="pj-label mb-2 flex items-center gap-2"><FileText size={12} /> Documents</div>
          {(docs?.documents || []).length === 0 && (
            <div className="mono text-[11px] text-[rgb(var(--dim))]">No documents yet.</div>
          )}
          {(docs?.documents || []).map((d) => (
            <div key={d.id} className="mono text-[12px] py-1 flex items-center justify-between gap-2" data-testid={`drawer-doc-${d.id}`}>
              <span className="capitalize truncate">{(d.kind || "").replaceAll("_", " ")}</span>
              <span className="flex items-center gap-2 shrink-0">
                <StatusPill state={d.status} />
                {d.share_url && (
                  <a href={d.share_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))]">
                    View
                  </a>
                )}
              </span>
            </div>
          ))}
        </section>

        {/* Payments list (new endpoint) + Refund (payment_id) */}
        <section className="pj-card p-4 mb-4" data-testid="drawer-payments">
          <div className="pj-label mb-2 flex items-center gap-2"><CreditCard size={12} /> Payments</div>
          {payments.length === 0 && (
            <div className="mono text-[11px] text-[rgb(var(--dim))]">No payments yet.</div>
          )}
          {payments.map((p) => (
            <div key={p.id} className="mono text-[12px] py-1.5 flex items-center justify-between gap-2" data-testid={`drawer-payment-${p.id}`}>
              <span className="min-w-0 truncate">
                {fmtUSD(p.amount_usd)} · {p.payment_method || "—"}
                {p.masked_last4 ? ` ····${p.masked_last4}` : ""}
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <StatusPill state={p.status} />
                {canRecord && p.status === "captured" && (
                  <button
                    type="button"
                    data-testid={`btn-refund-${p.id}`}
                    onClick={() => refund(p.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.12em] border border-[rgb(var(--border-strong))] hover:border-[rgb(196,52,44)] hover:text-[rgb(196,52,44)]"
                  >
                    <Undo2 size={10} /> Refund
                  </button>
                )}
              </span>
            </div>
          ))}
        </section>

        {/* Record Payment — admin/finance only (Virtual Terminal / wire / ach) */}
        {canRecord ? (
          <RecordPaymentForm bookingId={booking.id} payments={payments} onRecorded={refresh} />
        ) : (
          <div className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--muted-2))]" data-testid="record-payment-gated">
            Record payment / refund requires the admin or finance role.
          </div>
        )}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record Payment form — POST /api/ops/bookings/{id}/record-payment.
// Idempotent-replay UX: the backend returns the EXISTING captured payment when
// the booking is already paid; we detect that and say so instead of "recorded".
// Exported for unit tests.
// ---------------------------------------------------------------------------
export function RecordPaymentForm({ bookingId, payments = [], onRecorded }) {
  const [method, setMethod] = useState("card_terminal");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [last4, setLast4] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const existing = payments.find((p) => p.status === "captured");
      const pay = await ops.recordPayment(bookingId, {
        method,
        amount_usd: amount === "" ? null : Number(amount),
        reference: reference || null,
        last4: last4 || null,
      });
      const replay = Boolean(existing && existing.id === pay.id);
      setResult({ payment: pay, replay });
      toast.success(
        replay
          ? "Already paid — existing captured payment returned (idempotent, no double payout)"
          : "Payment recorded — booking confirmed, receipt issued",
      );
      onRecorded?.(pay);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="pj-card p-4" data-testid="record-payment-form">
      <div className="pj-label mb-2">Record payment · Virtual Terminal / wire</div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block min-w-0">
          <span className="pj-label block mb-1" style={{ fontSize: 9 }}>Method</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            data-testid="record-payment-method"
            className="w-full mono text-[12px] px-2 py-2"
            style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
          >
            <option value="card_terminal">Card (Virtual Terminal)</option>
            <option value="wire">Wire</option>
            <option value="ach">ACH</option>
          </select>
        </label>
        <label className="block min-w-0">
          <span className="pj-label block mb-1" style={{ fontSize: 9 }}>Amount USD (blank = full)</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="record-payment-amount"
            className="w-full mono text-[12px] px-2 py-2"
            style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
          />
        </label>
        <label className="block min-w-0">
          <span className="pj-label block mb-1" style={{ fontSize: 9 }}>Reference (VT txn / wire ref)</span>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            data-testid="record-payment-reference"
            className="w-full mono text-[12px] px-2 py-2"
            style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
          />
        </label>
        <label className="block min-w-0">
          <span className="pj-label block mb-1" style={{ fontSize: 9 }}>Card last4 (VT only)</span>
          <input
            type="text"
            value={last4}
            onChange={(e) => setLast4(e.target.value)}
            maxLength={4}
            data-testid="record-payment-last4"
            className="w-full mono text-[12px] px-2 py-2"
            style={{ background: "rgb(var(--surface))", border: "1px solid rgb(var(--ln))", color: "rgb(var(--fg))" }}
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="record-payment-submit"
        className="mt-3 mono w-full px-3 py-2 text-[11px] uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-40"
        style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
      >
        {busy ? "Recording…" : "Record payment"}
      </button>
      {result && (
        <div className="mt-3 mono text-[11px]" data-testid="record-payment-result">
          <StatusPill state={result.payment.status} />{" "}
          {result.replay
            ? "Idempotent replay — this booking was already paid; the existing captured payment was returned (no double payout)."
            : `Recorded ${fmtUSD(result.payment.amount_usd)} via ${result.payment.payment_method}.`}
        </div>
      )}
    </form>
  );
}
